import { createContext, useContext, useReducer, useMemo, useCallback, useEffect } from "react";
import type { Job } from "../../../../../models";
import type {
	WizardContextValue,
	StepConfig,
	WizardIssues,
	FormContextProps,
	WizardConfigurationState,
	InitialGeoData,
} from "./WizardContainer";
import {
	wizardReducer,
	createInitialWizardState,
	createInitialConfigurationState,
	canProceedFromStep,
	getIssuesForStep,
	getUnassignedJobCount,
} from "./hooks/useWizardReducer";
import { WIZARD_STEP_TITLES, WIZARD_STEP_DESCRIPTIONS } from "./WizardContainer";
import {
	detectOverlappingBoundaries,
	detectDuplicateBoundaries,
	detectInternalDuplicates,
	detectOverlappingZones,
	detectUnmatchedJobs,
} from "./utilities/geospatialValidation";
import { hasPrimaryFieldErrors } from "./WizardContext.helpers";

// ============================================================================
// Context
// ============================================================================

const WizardContext = createContext<WizardContextValue | null>(null);

// ============================================================================
// Hook
// ============================================================================

export function useWizardContext(): WizardContextValue {
	const context = useContext(WizardContext);
	if (!context) {
		throw new Error("useWizardContext must be used within a WizardProvider");
	}
	return context;
}

// ============================================================================
// Provider Props
// ============================================================================

interface WizardProviderProps {
	/** Initial geodata for configuration phase (alternative to initialJobs) */
	initialGeoData?: InitialGeoData;
	/** Initial jobs for validation phase */
	initialJobs?: Job[];
	children: React.ReactNode;
	/** Form context for JobDetailModal integration */
	formContext?: FormContextProps;
}

// ============================================================================
// Helper: Create initial state from props
// ============================================================================

/**
 * Creates geodata groups from uploaded files
 * This converts the GeoJSONDataMap format to our internal GeodataGroup format
 */
function createGeodataGroupsFromGeoData(geoData: InitialGeoData): WizardConfigurationState {
	const geodataGroups = Array.from(geoData.geoDataMap.entries()).map(([groupKey, geoDataList]) => {
		// Combine all features from all geoJSON objects in the group
		const allFeatures = geoDataList.flatMap((d) => d.geoJSON.features);

		// Determine geometry type based on first feature
		const firstFeature = allFeatures[0];
		const geomType = firstFeature?.geometry?.type;
		const isPoint = geomType === "Point" || geomType === "MultiPoint";
		const geometryType: "polygon" | "point" = isPoint ? "point" : "polygon";

		// Get all unique property names
		const propertyNames = Array.from(
			new Set(allFeatures.flatMap((f) => (f.properties ? Object.keys(f.properties) : []))),
		);

		// Create combined GeoJSON
		const combinedGeoJSON: import("geojson").FeatureCollection = {
			type: "FeatureCollection",
			features: allFeatures,
		};

		return {
			id: groupKey,
			displayName: groupKey.split("/").pop() || groupKey,
			filePaths: geoDataList.map((d) => d.groupKey),
			geometryType,
			featureCount: allFeatures.length,
			propertyNames,
			geoJSON: combinedGeoJSON,
		};
	});

	return {
		warnings: geoData.warnings,
		uploadedFiles: geoData.uploadedFiles,
		geodataGroups,
		propertyMappings: {},
		geoTypeSelections: {},
		excludedGroupIds: new Set(),
		selectedGroupIdsForMap: [],
		geoDataMap: geoData.geoDataMap,
		selectableGeoData: geoData.selectableGeoData,
		originalFiles: geoData.originalFiles,
	};
}

function createInitialState(props: WizardProviderProps) {
	// If initialGeoData is provided, start in configuration phase
	if (props.initialGeoData) {
		const configuration = createGeodataGroupsFromGeoData(props.initialGeoData);
		return createInitialConfigurationState(configuration);
	}

	// Otherwise, start in validation phase with jobs
	return createInitialWizardState(props.initialJobs || []);
}

// ============================================================================
// Provider Component
// ============================================================================

export function WizardProvider({ initialGeoData, initialJobs, children, formContext = {} }: WizardProviderProps) {
	// Initialize state based on props - configuration phase if geodata provided, validation if jobs
	const [state, dispatch] = useReducer(wizardReducer, { initialGeoData, initialJobs }, (props) =>
		createInitialState({ ...props, children: null }),
	);

	// Run validation when jobs change (only in validation phase)
	useEffect(() => {
		// Skip validation in configuration phase (no jobs yet)
		if (state.phase === "configuration") {
			return;
		}

		// Skip if no jobs
		if (state.jobs.length === 0) {
			return;
		}

		const runValidation = async () => {
			dispatch({ type: "SET_VALIDATING", isValidating: true });

			try {
				// Detect geospatial issues
				const overlaps = detectOverlappingBoundaries(state.jobs);
				const duplicateBoundaries = detectDuplicateBoundaries(state.jobs);
				const allInternalDuplicates = detectInternalDuplicates(state.jobs);
				const overlappingPoints = allInternalDuplicates
					.map((issue) => ({ ...issue, duplicateGroups: issue.duplicateGroups.filter((g) => g.type === "point") }))
					.filter((issue) => issue.duplicateGroups.length > 0);
				const overlappingZones = detectOverlappingZones(state.jobs);
				const unmatched = detectUnmatchedJobs(state.jobs);

				// Detect data validation issues
				// Using a simplified version - in production you'd use the full validation context
				const invalidData = state.jobs
					.map((job) => {
						// Basic validation - check for required fields
						const errors: Record<string, string> = {};
						if (!job.importGrowerName && !job.growerName) {
							errors.grower = "Grower is required";
						}
						if (!job.importFieldName && !job.fieldName) {
							errors.field = "Field is required";
						}
						if (Object.keys(errors).length > 0) {
							return {
								id: `invalid-${job.id}`,
								job,
								errors,
							};
						}
						return null;
					})
					.filter((issue): issue is NonNullable<typeof issue> => issue !== null)
					// Only show jobs with primary field errors in the wizard step
					.filter((issue) => hasPrimaryFieldErrors(issue.errors));

				const issues: WizardIssues = {
					overlaps,
					duplicateBoundaries,
					overlappingPoints,
					overlappingZones,
					unmatched,
					invalidData,
				};

				dispatch({ type: "SET_ISSUES", issues });
				dispatch({ type: "SET_VALIDATION_ERROR", error: null });
			} catch (error) {
				dispatch({
					type: "SET_VALIDATION_ERROR",
					error: error instanceof Error ? error.message : "Validation failed",
				});
			} finally {
				dispatch({ type: "SET_VALIDATING", isValidating: false });
			}
		};

		runValidation();
	}, [state.jobs, state.phase]);

	// Step configurations - configuration steps first, then validation steps
	const stepConfigs: StepConfig[] = useMemo(
		() => [
			// Configuration steps (Phase 1)
			{
				step: "file-validation",
				title: WIZARD_STEP_TITLES["file-validation"],
				description: WIZARD_STEP_DESCRIPTIONS["file-validation"],
				getIssues: () => [], // No "issues" in the validation sense
				hasIssues: () => false,
				isComplete: (s) => s.completedSteps.has("file-validation"),
			},
			{
				step: "property-mapping",
				title: WIZARD_STEP_TITLES["property-mapping"],
				description: WIZARD_STEP_DESCRIPTIONS["property-mapping"],
				getIssues: () => [],
				hasIssues: () => false,
				isComplete: (s) => s.completedSteps.has("property-mapping"),
			},
			{
				step: "geo-type-selection",
				title: WIZARD_STEP_TITLES["geo-type-selection"],
				description: WIZARD_STEP_DESCRIPTIONS["geo-type-selection"],
				getIssues: () => [],
				hasIssues: () => false,
				isComplete: (s) => s.completedSteps.has("geo-type-selection"),
			},
			// Validation steps (Phase 2) - unmatched first, then duplicates before overlaps to avoid false positives
			{
				step: "unmatched-features",
				title: WIZARD_STEP_TITLES["unmatched-features"],
				description: WIZARD_STEP_DESCRIPTIONS["unmatched-features"],
				getIssues: (s) => s.issues.unmatched,
				hasIssues: (s) => s.issues.unmatched.length > 0,
				isComplete: (s) =>
					s.issues.unmatched.length === 0 || s.issues.unmatched.every((i) => s.skippedIssueIds.has(i.id)),
			},
			{
				step: "duplicate-boundaries",
				title: WIZARD_STEP_TITLES["duplicate-boundaries"],
				description: WIZARD_STEP_DESCRIPTIONS["duplicate-boundaries"],
				getIssues: (s) => s.issues.duplicateBoundaries,
				hasIssues: (s) => s.issues.duplicateBoundaries.length > 0,
				isComplete: (s) =>
					s.issues.duplicateBoundaries.length === 0 ||
					s.issues.duplicateBoundaries.every((i) => s.skippedIssueIds.has(i.id)),
			},
			{
				step: "missing-invalid-data",
				title: WIZARD_STEP_TITLES["missing-invalid-data"],
				description: WIZARD_STEP_DESCRIPTIONS["missing-invalid-data"],
				getIssues: (s) => s.issues.invalidData,
				hasIssues: (s) => s.issues.invalidData.length > 0,
				isComplete: (s) =>
					s.issues.invalidData.length === 0 || s.issues.invalidData.every((i) => s.skippedIssueIds.has(i.id)),
			},
			{
				step: "branch-configuration",
				title: WIZARD_STEP_TITLES["branch-configuration"],
				description: WIZARD_STEP_DESCRIPTIONS["branch-configuration"],
				getIssues: () => [],
				hasIssues: () => false,
				isComplete: (s) => getUnassignedJobCount(s, "branch") === 0,
			},
			{
				step: "deal-configuration",
				title: WIZARD_STEP_TITLES["deal-configuration"],
				description: WIZARD_STEP_DESCRIPTIONS["deal-configuration"],
				getIssues: () => [], // No issue-based pagination
				hasIssues: () => false, // Always shown regardless
				isComplete: (s) => getUnassignedJobCount(s, "deal") === 0,
			},
			{
				step: "salesperson-assignment",
				title: WIZARD_STEP_TITLES["salesperson-assignment"],
				description: WIZARD_STEP_DESCRIPTIONS["salesperson-assignment"],
				getIssues: () => [],
				hasIssues: () => false,
				isComplete: (s) => !s.isSalespersonRequired || getUnassignedJobCount(s, "salesperson") === 0,
			},
			{
				step: "overlapping-boundaries",
				title: WIZARD_STEP_TITLES["overlapping-boundaries"],
				description: WIZARD_STEP_DESCRIPTIONS["overlapping-boundaries"],
				getIssues: (s) => s.issues.overlaps,
				hasIssues: (s) => s.issues.overlaps.length > 0,
				isComplete: (s) =>
					s.issues.overlaps.length === 0 || s.issues.overlaps.every((i) => s.skippedIssueIds.has(i.id)),
			},
			{
				step: "overlapping-zones",
				title: WIZARD_STEP_TITLES["overlapping-zones"],
				description: WIZARD_STEP_DESCRIPTIONS["overlapping-zones"],
				getIssues: (s) => s.issues.overlappingZones,
				hasIssues: (s) => s.issues.overlappingZones.length > 0,
				isComplete: (s) =>
					s.issues.overlappingZones.length === 0 || s.issues.overlappingZones.every((i) => s.skippedIssueIds.has(i.id)),
			},
			{
				step: "overlapping-points",
				title: WIZARD_STEP_TITLES["overlapping-points"],
				description: WIZARD_STEP_DESCRIPTIONS["overlapping-points"],
				getIssues: (s) => s.issues.overlappingPoints,
				hasIssues: (s) => s.issues.overlappingPoints.length > 0,
				isComplete: (s) =>
					s.issues.overlappingPoints.length === 0 ||
					s.issues.overlappingPoints.every((i) => s.skippedIssueIds.has(i.id)),
			},
			{
				step: "summary",
				title: WIZARD_STEP_TITLES["summary"],
				description: WIZARD_STEP_DESCRIPTIONS["summary"],
				getIssues: () => [],
				hasIssues: () => false,
				isComplete: () => true,
			},
		],
		[],
	);

	// Current step config
	const currentStepConfig = useMemo(
		() => stepConfigs.find((c) => c.step === state.currentStep) ?? null,
		[stepConfigs, state.currentStep],
	);

	// Can proceed to next step
	const canProceedToNextStep = useMemo(() => canProceedFromStep(state), [state]);

	// Get active (unskipped) issues for current step
	const getActiveIssuesForCurrentStep = useCallback(() => {
		return getIssuesForStep(state, state.currentStep).filter((issue) => !state.skippedIssueIds.has(issue.id));
	}, [state]);

	const value = useMemo(
		(): WizardContextValue => ({
			state,
			dispatch,
			stepConfigs,
			currentStepConfig,
			canProceedToNextStep,
			getActiveIssuesForCurrentStep,
			formContext,
		}),
		[state, stepConfigs, currentStepConfig, canProceedToNextStep, getActiveIssuesForCurrentStep, formContext],
	);

	return <WizardContext.Provider value={value}>{children}</WizardContext.Provider>;
}
